import React, { useState, useEffect } from "react";
import {
  X,
  Mail,
  CheckCircle2,
  ArrowLeft,
  RotateCcw,
  SquareMenu,
} from "lucide-react";
import "./CommonOTPModal.css";
import toast from "react-hot-toast";
import * as MenuService from "../Services/Masters/Menu/MenuService";
import { useNavigate } from "react-router-dom";

const CommonOTPModal = ({ onSuccess, storageKey, menuName }) => {
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);

  const navigate = useNavigate()

  useEffect(() => {
    let timer;
    if (resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const sendOtp = async () => {
    if (isSending || resendTimer > 0) return;

    setIsSending(true);

    const payload = {};
    MenuService.SendMenuOTP(payload)
      .then(({ data }) => {
        if (data.IsSuccess) {
          toast.success(data.ResponseMessage);
          setOtpSent(true);
          setResendTimer(30);
        } else {
          toast.error(data.ResponseMessage);
        }
      })
      .catch(() => {
        toast.error("Something went wrong");
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const validateOtp = async () => {
    if (otp.length < 4) return;

    setIsValidating(true);

    const payload = { Otp: otp };

    MenuService.VerifyOtp(payload)
      .then(({ data }) => {
        if (data.IsSuccess) {
          toast.success("Your OTP has been verified!");
          setShowSuccess(true);

          setTimeout(() => {
            const otpData = {
              verified: true,
              MenuName: menuName
            };

            sessionStorage.setItem(storageKey, JSON.stringify(otpData));

            onSuccess(otpData);
          }, 1500);
        } else {
          toast.error(data.ResponseMessage);
          setOtp("");
        }
      })
      .catch(() => {
        toast.error("Invalid OTP. Please try again.");
      })
      .finally(() => {
        setIsValidating(false);
      });
  };

  // const handleBack = () => {
  //   setOtpSent(false);
  //   setOtp("");
  //   setIsSending(false);
  //   setShowSuccess(false);
  // };

  const handleClose = () => {
    onSuccess(null);
  };

  const formatTime = (seconds) => {
    return `${Math.floor(seconds / 60)}:${(seconds % 60)
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <>
      {/* Modal Backdrop */}
      <div className="otp-otp-backdrop d-flex align-items-center justify-content-center">
        <div className="container-fluid">
          <div className="row justify-content-center">
            <div className="col-12 col-sm-10 col-md-8 col-lg-5 col-xl-4">
              {/* Main Modal */}
              <div className="otp-otp-modal card border-0 shadow-lg">
                {/* Header */}
                <div className="otp-otp-header">
                  <button
                    onClick={handleClose}
                    className="otp-close-btn position-absolute"
                    type="button"
                    aria-label="Close"
                  >
                    <X size={20} />
                  </button>

                  <div className="d-flex justify-content-center align-items-center mb-2">
                    <h2 className="otp-otp-title mb-0">OTP Verification</h2>
                  </div>
                  <p className="header-subtitle text-center mb-0">
                    OTP verification is required to access this page.
                  </p>
                </div>

                {/* Body */}
                <div className="otp-otp-body">
                  {showSuccess ? (
                    // Success State
                    <div className="text-center py-4">
                      <div className="success-icon mx-auto mb-3">
                        <CheckCircle2 size={48} />  
                      </div>
                      <h3 className="success-title mb-2">
                        Verification Successful!
                      </h3>
                      <p className="success-subtitle">
                        You have been successfully authenticated.
                      </p>
                    </div>
                  ) : !otpSent ? (
                    // Email Input Phase
                    <div>
                      <div className="d-grid">
                        <button
                          onClick={sendOtp}
                          disabled={isSending || resendTimer > 0}
                          className="otp-primary-btn custom-sumbitbtn"
                        >
                          {isSending ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Sending OTP...
                            </>
                          ) : resendTimer > 0 ? (
                            `Resend in ${formatTime(resendTimer)}`
                          ) : (
                            <>
                              <Mail size={20} className="me-2" />
                              Send OTP on Email
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-4">
                        <label className="form-label otp-label">
                          Verification Code
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={otp}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            if (val.length <= 6) setOtp(val);
                          }}
                          placeholder="0000"
                          maxLength="4"
                          className="form-control otp-code-input text-center"
                          onKeyPress={(e) => e.key === "Enter" && validateOtp()}
                        />
                      </div>

                      <div className="d-flex gap-3 justify-content-around align-items-center mb-4">
                        {/* <button
                          onClick={handleBack}
                          className="otp-secondary-btn"
                        >
                          Back
                        </button> */}
                        <button
                          onClick={validateOtp}
                          disabled={otp.length < 4 || isValidating}
                          className="otp-primary-btn"
                        >
                          {isValidating ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Validating...
                            </>
                          ) : (
                            <>
                              Verify
                            </>
                          )}
                        </button>
                      </div>

                      <div className="d-flex justify-content-center">
                        <button
                          onClick={sendOtp}
                          disabled={isSending || resendTimer > 0}
                          className="otp-link-btn text-center"
                        >
                          {isSending ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Resending...
                            </>
                          ) : resendTimer > 0 ? (
                            `Resend in ${formatTime(resendTimer)}`
                          ) : (
                            <>
                              <RotateCcw size={16} className="me-2" />
                              Resend OTP
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommonOTPModal;
